import { TranslationEdit, TranslationListArgs } from '@/types'
import { useFetchWithInterceptor } from '@/use'
import { toRaw } from 'vue'

export async function fetchTranslations(lang: string): Promise<Record<string, any>> {
  const url = `${import.meta.env.VITE_API_LANG_URL}/translations/${lang}?prefix=MetabolicLang&fallbacks=true`
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`Failed to fetch translations for ${lang} language`)
  }
  return await response.json()
}

export async function getTranslationsList(args: TranslationListArgs): Promise<Record<string, any>> {
  const query = new URLSearchParams()
  query.append('offset', args.offset.toString())
  query.append('take', args.take.toString())
  query.append('language', args.language)
  if (args.modifiedFrom && args.modifiedFrom.length) {
    query.append('modifiedFrom', args.modifiedFrom)
  }
  if (args.isApproved != null) {
    query.append('isApproved', args.isApproved ? 'true' : 'false')
  }
  if (args.prefix && args.prefix.length) {
    args.prefix.forEach((prefix) => {
      query.append('prefix', prefix)
    })
  }
  if (args.text && args.text.length) {
    args.text.forEach((text) => {
      query.append('text', text)
    })
  }
  if (args.key && args.key.length) {
    args.key.forEach((key) => {
      query.append('key', key)
    })
  }
  if (args.sortBy) {
    query.append('order', `${args.sortBy} ${args.sortingOrder}`)
  }

  const { request, response } = useFetchWithInterceptor<Record<string, any>>(
    `${import.meta.env.VITE_API_LANG_URL}/translation-list/${args.language}?${query.toString()}`,
  )

  await request()
  if (response.value) {
    return toRaw(response.value)
  } else {
    throw new Error('Response is undefined')
  }
}

export async function getTranslationLanguageList(): Promise<string[]> {
  const url = `${import.meta.env.VITE_API_LANG_URL}/languages`
  const { request, response } = useFetchWithInterceptor<string[]>(url)
  await request()
  if (response.value) {
    return toRaw(response.value)
  } else {
    throw new Error('Response is undefined')
  }
}

export async function getTranslationPrefixList(): Promise<string[]> {
  const url = `${import.meta.env.VITE_API_LANG_URL}/prefixes`
  const { request, response } = useFetchWithInterceptor<string[]>(url)
  await request()
  if (response.value) {
    return toRaw(response.value)
  } else {
    throw new Error('Response is undefined')
  }
}

export const saveTranslation = async (translation: TranslationEdit, lang: string): Promise<number> => {
  const payload = {
    [translation.key]: translation.text,
  }

  const { response, request } = useFetchWithInterceptor<number>(
    `${import.meta.env.VITE_API_LANG_URL}/translations/${lang}?approved=true`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  )
  await request()
  if (response.value) {
    return toRaw(response.value)
  } else {
    throw new Error('Response is undefined')
  }
}
